import type { ReportUpdateParams, ReportTitle, ReportContent, IDocument } from '@/domains/project/types';
import { FactoryPsHttpSSE } from '@patsnap-utils/sse';
import type { PsHttpSSE } from '@patsnap-utils/sse';
import type { PatSnapCommonResponseData } from '@patsnap/tahiti-core/common/ps-http';
import { PsHttpService } from '@patsnap/tahiti-core/common/ps-http';
import { injectable } from '@patsnap/tahiti-core/shared/inversify';
import { injectService } from '@patsnap/tahiti-core';

export interface ReportApi {
    getReportStream<T>(projectId: string): PsHttpSSE<T>
    refreshReport(projectId: string): Promise<void>
    updateReport(params: ReportUpdateParams): Promise<ReportTitle | ReportContent>
    getReportReference(projectId: string, refId: string): Promise<IDocument>
}

@injectable()
export class ReportApiImpl extends PsHttpService implements ReportApi {
    get options() {
        return {
            baseURL: `${import.meta.env.VITE_EUREKA_SERVICE_URI}/eureka/work/project/report`,
            sseURL: `${import.meta.env.VITE_EUREKA_SERVICE_URI}/eureka`,
        }
    }

    getReportStream<T>(projectId: string) {
        const sseClient = injectService<FactoryPsHttpSSE>(FactoryPsHttpSSE);
        return sseClient<T>().init({
            url: `${this.options.sseURL}/sse/work/project/report`,
            data: { work_project_id: projectId },
        });
    }

    refreshReport(projectId: string) {
        return this.post<PatSnapCommonResponseData<void>>({
            url: '/refresh',
            data: {
                work_project_id: projectId,
            },
        }).then((res) => {
            return res.data as void
        });
    }

    updateReport(params: ReportUpdateParams) {
        const { projectId, text, title, index } = params
        return this.post<PatSnapCommonResponseData<ReportTitle | ReportContent>>({
            url: '/update',
            data: { work_project_id: projectId, text, title, index },
        }).then((res) => {
            return res.data as ReportTitle
        });
    }

    getReportReference(projectId: string, refId: string) {
        return this.post<PatSnapCommonResponseData<IDocument>>({
            url: '/reference',
            data: { work_project_id: projectId, ref_id: refId },
        }).then((res) => {
            return res.data as IDocument
        });
    }
}

export const ReportApi = Symbol('ReportApi');
